import type React from 'react';

import type { FormattedMessage } from 'js/lib/coursera.react-intl';

import countries from 'bundles/page/common/countries';
import type { BillingAddressFieldName } from 'bundles/payments-common/common/types';
import { validPostalCodeCA } from 'bundles/payments-common/lib/validate';

import _t from 'i18n!nls/payments';

export type FieldType = 'text' | 'menu';

export type FieldWidth = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export type FieldSize = {
  mobile: FieldWidth;
  desktop: FieldWidth;
};

export const FULL_WIDTH: FieldSize = {
  mobile: 12,
  desktop: 12,
};

export type BaseFieldDescriptor = {
  type: FieldType;
  name: BillingAddressFieldName;
  placeholder?: string;
  size?: FieldSize;
};

export type TextFieldDescriptor = BaseFieldDescriptor & {
  type: 'text';
  pattern?: string;
  maxLength?: number;
};

export type MenuOption = {
  label: string | React.ReactElement<typeof FormattedMessage>;
  value: string;
};

export type MenuFieldDescriptor = BaseFieldDescriptor & {
  type: 'menu';
  options: MenuOption[];
};

export type FieldDescriptor = TextFieldDescriptor | MenuFieldDescriptor;

/*
 * Field Definitions
 * wrapped in a function for _t
 */

const getFieldDescriptors = (): { [defName: string]: FieldDescriptor } => {
  const longCountryField: FieldDescriptor = {
    type: 'menu',
    name: 'country',
    placeholder: _t('Select your country'),
    size: {
      mobile: 12,
      desktop: 12,
    },
    options: countries.map((nameAndCode: $TSFixMe) => ({
      label: nameAndCode.name,
      value: nameAndCode.code,
    })),
  };

  const shortCountryField: FieldDescriptor = {
    // @ts-expect-error TSMIGRATION-3.9
    type: 'menu',
    ...longCountryField,
    size: {
      mobile: 12,
      desktop: 8,
    },
  };

  const zipcodeField: FieldDescriptor = {
    type: 'text',
    name: 'zipcode',
    placeholder: _t('Zipcode'),
    size: {
      mobile: 12,
      desktop: 4,
    },
    pattern: '[0-9]{5}',
    maxLength: 5,
  };

  const postalCodeFieldCA: FieldDescriptor = {
    type: 'text',
    name: 'postalCode',
    placeholder: 'XXX XXX',
    size: {
      mobile: 12,
      desktop: 4,
    },
    pattern: validPostalCodeCA.toString(),
    maxLength: 7,
  };

  return {
    longCountryField,
    shortCountryField,
    zipcodeField,
    postalCodeFieldCA,
  };
};

/*
 * Now assemble them together, based on country.
 */

export const getLocationFields = ({ country }: { country?: string }): FieldDescriptor[] => {
  const { longCountryField, shortCountryField, zipcodeField, postalCodeFieldCA } = getFieldDescriptors();
  switch (country) {
    case 'US':
      // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      return [shortCountryField, zipcodeField];
    case 'CA':
      // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      return [shortCountryField, postalCodeFieldCA];
    default:
      // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      return [longCountryField];
  }
};

export default {
  getLocationFields,
};
